import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Boxes,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Code2,
  Cpu,
  Layers3,
  Loader2,
  PackageCheck,
  ShieldCheck,
  Sparkles,
  Store,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  return bytes >= 1_000_000_000
    ? `${(bytes / 1_000_000_000).toFixed(1)} GB`
    : `${Math.round(bytes / 1_000_000)} MB`;
};

const money = (value, currency = "IDR") => {
  const amount = Number(value || 0);
  if (!amount) return "Gratis";
  const isUsd = currency === "USD";
  if (!isUsd) return `Rp${amount.toLocaleString("id-ID")}`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const PLAN_PRESENTATION = {
  free: {
    eyebrow: "Eksplorasi",
    description: "Selesaikan desain, sound, dan preview plugin sebelum menentukan jalur rilis.",
    build: "Tanpa build",
    platform: "Designer penuh",
    license: "Preview langsung",
    icon: Sparkles,
    accent: "sky",
  },
  personal: {
    eyebrow: "Untuk pemakaian sendiri",
    description: "Jadikan satu project sebagai VST3 pribadi dengan satu kesempatan build.",
    build: "1 plugin · 1 build",
    platform: "Windows/macOS",
    license: "1 perangkat",
    icon: Cpu,
    accent: "violet",
  },
  creator_personal: {
    eyebrow: "Lebih banyak build",
    description: "Hak seperti Personal dengan tiga kesempatan build untuk satu project.",
    build: "1 plugin · 3 build",
    platform: "Windows/macOS",
    license: "1 perangkat",
    icon: Layers3,
    accent: "sky",
  },
  creator: {
    eyebrow: "Paling populer",
    description: "Satu plugin komersial dengan pool build untuk penyempurnaan dan rilis lintas platform.",
    build: "1 plugin · 12 build",
    platform: "Windows/macOS",
    license: "100 serial",
    icon: PackageCheck,
    accent: "rose",
    featured: true,
  },
  studio: {
    eyebrow: "Untuk skala bisnis",
    description: "Kelola hingga tiga plugin komersial dengan satu pool build bersama untuk studio atau publisher.",
    build: "3 plugin · 24 build",
    platform: "Windows/macOS",
    license: "1.000/plugin",
    icon: Code2,
    accent: "amber",
  },
};

const accentClasses = {
  sky: {
    border: "border-sky-300/20 hover:border-sky-300/35",
    glow: "from-sky-400/[.10]",
    icon: "border-sky-300/20 bg-sky-300/10 text-sky-200",
    eyebrow: "text-sky-200",
    check: "text-sky-300",
  },
  violet: {
    border: "border-violet-300/20 hover:border-violet-300/35",
    glow: "from-violet-400/[.10]",
    icon: "border-violet-300/20 bg-violet-300/10 text-violet-200",
    eyebrow: "text-violet-200",
    check: "text-violet-300",
  },
  rose: {
    border: "border-[#fb7185]/45 hover:border-[#fb7185]/70",
    glow: "from-[#e11d48]/[.18]",
    icon: "border-[#fb7185]/30 bg-[#e11d48]/15 text-[#fb7185]",
    eyebrow: "text-[#fb7185]",
    check: "text-[#fb7185]",
  },
  amber: {
    border: "border-amber-300/25 hover:border-amber-300/45",
    glow: "from-amber-300/[.12]",
    icon: "border-amber-300/25 bg-amber-300/10 text-amber-200",
    eyebrow: "text-amber-200",
    check: "text-amber-300",
  },
};

const comparisonRows = [
  { label: "Tujuan", free: "Desain & preview", personal: "Pemakaian pribadi", creator_personal: "Pemakaian pribadi", creator: "Penjualan komersial", studio: "Bisnis & publisher" },
  { label: "Jumlah plugin", free: "—", personal: "1 plugin", creator_personal: "1 plugin", creator: "1 plugin komersial", studio: "Maksimal 3 plugin komersial" },
  { label: "Platform build", free: "—", personal: "Windows/macOS", creator_personal: "Windows/macOS", creator: "Windows/macOS", studio: "Windows/macOS" },
  { label: "Build total", free: "0", personal: "1", creator_personal: "3", creator: "12", studio: "24 · pool bersama" },
  { label: "Serial termasuk", free: "—", personal: "1 perangkat", creator_personal: "1 perangkat", creator: "100 serial", studio: "1.000 per plugin" },
  { label: "Hak komersial", free: "—", personal: "—", creator_personal: "—", creator: "Termasuk", studio: "Termasuk" },
  { label: "API distribusi", free: "—", personal: "—", creator_personal: "—", creator: "—", studio: "Termasuk" },
];

function PlanCard({ plan, currency, busy, onCheckout }) {
  const view = PLAN_PRESENTATION[plan.id] || PLAN_PRESENTATION.free;
  const colors = accentClasses[view.accent];
  const Icon = view.icon;
  const discount = plan.id === "personal"
    && Number(plan.first_purchase_discount) > 0
    && Number(plan.normal_price) > Number(plan.price)
    ? Math.round((1 - Number(plan.price) / Number(plan.normal_price)) * 100)
    : 0;
  const displayPrice = plan.display_price ?? plan.price;
  const displayNormalPrice = plan.display_normal_price ?? plan.normal_price;

  return (
    <article className={`group relative flex min-h-full flex-col overflow-hidden rounded-[1.75rem] border bg-[#0a0a0d]/95 p-5 shadow-2xl shadow-black/20 transition duration-300 hover:-translate-y-1 md:p-6 ${colors.border}`}>
      <div className={`pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b ${colors.glow} to-transparent opacity-80`} />
      {view.featured && <div className="absolute right-4 top-4 rounded-full border border-[#fb7185]/30 bg-[#e11d48]/15 px-2.5 py-1 text-[9px] font-black uppercase tracking-[.14em] text-[#fb7185]">Rekomendasi</div>}
      <div className="relative">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl border ${colors.icon}`}><Icon className="h-5 w-5" /></div>
        <div className={`mt-5 text-[9px] font-black uppercase tracking-[.2em] ${colors.eyebrow}`}>{view.eyebrow}</div>
        <h3 className="mt-2 font-[Outfit] text-2xl font-black text-white">{plan.name}</h3>
        <p className="mt-2 min-h-[3.75rem] text-xs leading-5 text-zinc-500">{view.description}</p>

        <div className="mt-5 flex flex-col items-start gap-1 border-b border-white/[.07] pb-5">
          {discount > 0 && (
            <div className="font-[Outfit] text-3xl font-black tracking-tight text-zinc-400 line-through decoration-zinc-300/80 decoration-2">{money(displayNormalPrice, currency)}</div>
          )}
          <div className="font-[Outfit] text-3xl font-black tracking-tight text-white">{money(displayPrice, currency)}</div>
          {discount > 0 && (
            <p className="mt-1 text-[10px] font-semibold leading-4 text-emerald-300/90">Harga spesial untuk pembelian paket pertama Anda</p>
          )}
        </div>
        {discount > 0 && (
          <div className="mt-3 flex items-center gap-2 text-[10px]">
            <span className="rounded-full bg-emerald-300/10 px-2 py-1 font-black text-emerald-300">Hemat {discount}%</span>
          </div>
        )}

        <div className="mt-5 grid grid-cols-1 gap-2 text-[10px] font-bold text-zinc-300">
          {[view.build, view.platform, view.license].map((item) => <div key={item} className="min-w-0 break-words rounded-xl border border-white/[.07] bg-black/25 px-3 py-2.5">{item}</div>)}
        </div>

        <div className="mt-5 space-y-2.5">
          {(plan.features || []).map((feature) => (
            <div key={feature} className="flex gap-2.5 text-[11px] leading-5 text-zinc-400"><Check className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${colors.check}`} />{feature}</div>
          ))}
        </div>
      </div>

      <div className="relative mt-auto pt-6">
        {plan.product_id ? (
          <button type="button" onClick={() => onCheckout(plan.product_id)} disabled={busy === plan.product_id} className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-xs font-black transition disabled:opacity-40 ${view.featured ? "bg-[#e11d48] text-white shadow-lg shadow-[#e11d48]/20 hover:bg-[#be123c]" : "bg-white text-black hover:bg-zinc-200"}`}>
            {busy === plan.product_id ? <><Loader2 className="h-4 w-4 animate-spin" />Memproses…</> : <>Pilih {plan.name}<ArrowRight className="h-4 w-4" /></>}
          </button>
        ) : (
          <Link to="/vst-studio" className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[.04] px-4 py-3.5 text-xs font-black text-white transition hover:bg-white/[.08]">Mulai mendesain<ArrowRight className="h-4 w-4" /></Link>
        )}
        <p className="mt-3 text-center text-[9px] leading-4 text-zinc-700">{plan.product_id ? "Sekali bayar · tanpa perpanjangan otomatis" : "Gratis · tidak memerlukan kartu pembayaran"}</p>
      </div>
    </article>
  );
}

export default function VstMakerBilling() {
  const { isCustomer, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [billing, setBilling] = useState(null);
  const [methods, setMethods] = useState({});
  const [busy, setBusy] = useState("");
  const [currencyPreference] = useState(() => {
    const saved = (localStorage.getItem("ts_display_currency") || "").toUpperCase();
    return ["IDR", "USD"].includes(saved) ? saved : "";
  });
  const currencyQuery = currencyPreference
    ? `?currency=${encodeURIComponent(currencyPreference)}`
    : "";

  useEffect(() => {
    Promise.all([api.get(`/vst-maker/billing/catalog${currencyQuery}`), api.get("/checkout/payment-methods")])
      .then(([catalogResponse, methodsResponse]) => {
        setCatalog(catalogResponse.data);
        setMethods(methodsResponse.data);
      })
      .catch((error) => toast.error(error.userMessage || "Gagal memuat paket VST"));
  }, [currencyQuery]);

  useEffect(() => {
    if (!authLoading && isCustomer) {
      api.get(`/customer/vst-maker/billing${currencyQuery}`)
        .then(({ data }) => {
          setBilling(data);
          if (data.catalog) setCatalog(data.catalog);
        })
        .catch(() => setBilling(null));
    }
  }, [authLoading, currencyQuery, isCustomer]);

  const checkout = async (productId) => {
    if (!isCustomer) {
      navigate("/login", { state: { from: "/vst-maker/billing" } });
      return;
    }
    setBusy(productId);
    const payload = { product_id: productId, origin_url: window.location.origin, platform: "product" };
    try {
      if (methods.doku_enabled) {
        const { data } = await api.post("/checkout/doku/session", payload);
        window.location.assign(data.url);
        return;
      }
      if (methods.midtrans_enabled) {
        const { data } = await api.post("/checkout/midtrans/session", payload);
        window.location.assign(data.redirect_url);
        return;
      }
      throw new Error("Gateway pembayaran IDR belum aktif.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || "Gagal memulai pembayaran");
      setBusy("");
    }
  };

  if (!catalog) {
    return <div className="min-h-screen bg-[#050507] pt-40 text-center text-zinc-500"><Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" />Memuat paket VST…</div>;
  }

  const plans = catalog.plans || [];
  const displayCurrency = catalog.display_currency || "IDR";
  const standardPlans = plans.filter((plan) => plan.id !== "partner");
  const partnerPlan = plans.find((plan) => plan.id === "partner");
  const purchases = billing?.business_entitlements || [];
  const editorEntitlement = billing?.entitlement;
  const availablePluginSlots = purchases.reduce((total, entitlement) => {
    const projectLimit = Number(entitlement.project_limit || 0);
    if (!projectLimit) return total;
    const boundProjects = new Set([
      ...(entitlement.bound_project_ids || []),
      entitlement.bound_project_id,
    ].filter(Boolean));
    return total + Math.max(0, projectLimit - boundProjects.size);
  }, 0);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050507] px-4 pb-20 pt-24 text-zinc-200 md:px-8 md:pt-28">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[34rem] w-[70rem] -translate-x-1/2 rounded-full bg-[#e11d48]/[.08] blur-[140px]" />
      <div className="pointer-events-none absolute -left-52 top-[32rem] h-96 w-96 rounded-full bg-sky-500/[.06] blur-[120px]" />
      <div className="relative mx-auto max-w-7xl">
        <Link to="/vst-maker" className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-500 transition hover:text-white"><ArrowLeft className="h-4 w-4" />Kembali ke VST Maker</Link>

        <header className="mx-auto mt-10 max-w-4xl text-center">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-[#fb7185]/20 bg-[#e11d48]/[.07] px-3 py-1.5 text-[9px] font-black uppercase tracking-[.2em] text-[#fb7185]"><BadgeCheck className="h-3.5 w-3.5" />Paket VST sekali bayar</div>
          <h1 className="mt-6 font-[Outfit] text-4xl font-black tracking-[-.035em] text-white sm:text-5xl md:text-7xl">Dari project menjadi <span className="bg-gradient-to-r from-[#fb7185] to-[#f5c76b] bg-clip-text text-transparent">plugin siap rilis.</span></h1>
          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-zinc-400 md:text-base">Pilih kapasitas untuk satu hingga tiga plugin. Setiap paket menjelaskan jumlah plugin, kuota build Windows/macOS, lisensi, dan jalur distribusinya.</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-2 text-[10px] font-bold text-zinc-400">
            {["Harga tetap per plugin", "Build VST3 native", "Lisensi termasuk", "Tanpa perpanjangan otomatis"].map((item) => <span key={item} className="inline-flex items-center gap-1.5 rounded-full border border-white/[.08] bg-white/[.035] px-3 py-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />{item}</span>)}
          </div>
        </header>

        {isCustomer && (
          <section className="mt-10 overflow-hidden rounded-3xl border border-white/[.08] bg-[#0a0a0d]/90 shadow-xl shadow-black/20">
            <div className="grid md:grid-cols-[1.25fr_1fr_1fr]">
              <article className="border-b border-white/[.07] p-5 md:border-b-0 md:border-r md:p-6"><div className="text-[9px] font-black uppercase tracking-[.18em] text-[#fb7185]">Hak build aktif</div><div className="mt-2 text-xl font-black text-white">{editorEntitlement?.business_plan?.plan_name || (editorEntitlement?.partner_active ? "Marketplace Partner" : "Free Builder")}</div><div className="mt-1 text-[10px] text-zinc-600">Paket terbaru pada akun Anda</div></article>
              <article className="border-b border-white/[.07] p-5 md:border-b-0 md:border-r md:p-6"><div className="text-[9px] font-black uppercase tracking-[.18em] text-zinc-600">Siap dipakai</div><div className="mt-2 text-xl font-black text-white">{availablePluginSlots} <span className="text-xs font-semibold text-zinc-600">slot plugin</span></div><div className="mt-1 text-[10px] text-zinc-600">Kapasitas project yang masih tersedia</div></article>
              <article className="p-5 md:p-6"><div className="text-[9px] font-black uppercase tracking-[.18em] text-zinc-600">Penyimpanan sample</div><div className="mt-2 text-xl font-black text-white">{editorEntitlement ? `${formatBytes(editorEntitlement.storage_used_bytes)} / ${formatBytes(editorEntitlement.storage_limit_bytes)}` : "—"}</div><div className="mt-1 text-[10px] text-zinc-600">Privat di akun Anda</div></article>
            </div>
          </section>
        )}

        <section className="mt-16">
          <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
            <div><div className="text-[10px] font-black uppercase tracking-[.22em] text-[#f5c76b]">Pilih jalur rilis</div><h2 className="mt-2 font-[Outfit] text-3xl font-black tracking-tight text-white md:text-4xl">Paket yang tumbuh bersama plugin Anda</h2><p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-500">Mulai gratis, gunakan Personal untuk kebutuhan sendiri, atau pilih hak komersial ketika siap menjual.</p></div>
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/[.08] bg-white/[.035] px-4 py-2.5 text-[10px] font-bold text-zinc-400"><ShieldCheck className="h-4 w-4 text-emerald-300" />Pembayaran aman melalui gateway resmi</div>
          </div>

          <div className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {standardPlans.map((plan) => <PlanCard key={plan.id} plan={plan} currency={displayCurrency} busy={busy} onCheckout={checkout} />)}
          </div>
        </section>

        {partnerPlan && (
          <section className="relative mt-6 overflow-hidden rounded-[2rem] border border-[#fb7185]/25 bg-gradient-to-br from-[#16090e] via-[#0c090c] to-[#08090c] p-6 shadow-2xl shadow-black/30 md:p-8">
            <div className="pointer-events-none absolute right-0 top-0 h-72 w-72 rounded-full bg-[#e11d48]/15 blur-[100px]" />
            <div className="relative grid gap-8 lg:grid-cols-[1.25fr_.75fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#fb7185]/25 bg-[#e11d48]/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-[.18em] text-[#fb7185]"><Store className="h-3.5 w-3.5" />Alternatif tanpa biaya awal</div>
                <h2 className="mt-5 font-[Outfit] text-3xl font-black text-white md:text-4xl">Rilis sebagai Marketplace Partner.</h2>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-400">TripleSide menanggung jalur build di muka. Plugin dipasarkan eksklusif melalui marketplace kami, serial dibuat otomatis, dan pendapatan dibagi dari setiap penjualan.</p>
                <div className="mt-5 flex flex-wrap gap-2">{(partnerPlan.features || []).map((feature) => <span key={feature} className="inline-flex items-center gap-1.5 rounded-full border border-white/[.08] bg-black/20 px-3 py-2 text-[10px] font-semibold text-zinc-300"><Check className="h-3.5 w-3.5 text-[#fb7185]" />{feature}</span>)}</div>
              </div>
              <div className="rounded-2xl border border-white/[.08] bg-black/25 p-5">
                <div className="text-[9px] font-black uppercase tracking-[.18em] text-zinc-600">Biaya di muka</div>
                <div className="mt-2 font-[Outfit] text-4xl font-black text-white">Gratis</div>
                <div className="mt-2 text-[11px] leading-5 text-zinc-500">Model bagi hasil · persetujuan terms diperlukan</div>
                <Link to="/vst-maker/partner" className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#e11d48] px-4 py-3.5 text-xs font-black text-white transition hover:bg-[#be123c]">Pelajari jalur Partner<ChevronRight className="h-4 w-4" /></Link>
              </div>
            </div>
          </section>
        )}

        <section className="mt-16">
          <div className="text-center"><div className="text-[10px] font-black uppercase tracking-[.22em] text-zinc-600">Perbandingan paket</div><h2 className="mt-2 font-[Outfit] text-3xl font-black text-white">Pilih paket sesuai kebutuhan Anda</h2></div>
          <div className="mt-7 overflow-x-auto rounded-3xl border border-white/[.08] bg-[#0a0a0d]">
            <table className="w-full min-w-[760px] text-left text-xs">
              <thead><tr className="border-b border-white/[.08] bg-white/[.025]"><th className="px-5 py-4 text-[9px] font-black uppercase tracking-[.16em] text-zinc-600">Fitur</th>{standardPlans.map((plan) => <th key={plan.id} className={`px-5 py-4 font-black ${plan.id === "creator" ? "text-[#fb7185]" : "text-white"}`}>{plan.name}</th>)}</tr></thead>
              <tbody>{comparisonRows.map((row) => <tr key={row.label} className="border-b border-white/[.055] last:border-0"><th className="px-5 py-4 font-semibold text-zinc-500">{row.label}</th>{standardPlans.map((plan) => <td key={plan.id} className={`px-5 py-4 ${plan.id === "creator" ? "bg-[#e11d48]/[.025] text-zinc-200" : "text-zinc-400"}`}>{row[plan.id]}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </section>

        <section className="mt-10 grid gap-4 md:grid-cols-3">
          <article className="rounded-2xl border border-white/[.08] bg-[#0a0a0d] p-5"><Layers3 className="h-5 w-5 text-sky-300" /><h3 className="mt-3 font-bold text-white">Tidak dihitung dari kompleksitas</h3><p className="mt-2 text-xs leading-5 text-zinc-500">Jumlah komponen, DSP, sample, dan template tidak mengubah harga paket.</p></article>
          <article className="rounded-2xl border border-white/[.08] bg-[#0a0a0d] p-5"><ShieldCheck className="h-5 w-5 text-emerald-300" /><h3 className="mt-3 font-bold text-white">Lisensi siap digunakan</h3><p className="mt-2 text-xs leading-5 text-zinc-500">Personal dan Creator mendapat satu perangkat; Creator Commercial dan Studio Commercial memperoleh kuota serial komersial.</p></article>
          <article className="rounded-2xl border border-white/[.08] bg-[#0a0a0d] p-5"><CircleDollarSign className="h-5 w-5 text-amber-300" /><h3 className="mt-3 font-bold text-white">Butuh serial tambahan?</h3><p className="mt-2 text-xs leading-5 text-zinc-500">Harga penambahan kuota belum tersedia secara self-service. Hubungi tim kami untuk kebutuhan di atas 100 atau 1.000 serial.</p></article>
        </section>

        <section className="mt-16 rounded-[2rem] border border-white/[.08] bg-gradient-to-r from-white/[.045] to-transparent p-6 md:p-8">
          <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div><div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[.18em] text-[#fb7185]"><Boxes className="h-4 w-4" />Mulai dari project Anda</div><h2 className="mt-3 font-[Outfit] text-2xl font-black text-white md:text-3xl">Belum yakin paket mana yang tepat?</h2><p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">Bangun dan uji plugin secara gratis terlebih dahulu. Anda baru memilih paket saat project siap diekspor.</p></div>
            <div className="flex flex-wrap gap-3"><Link to="/vst-studio" className="inline-flex items-center gap-2 rounded-xl bg-[#e11d48] px-5 py-3 text-xs font-black text-white shadow-lg shadow-[#e11d48]/20 transition hover:bg-[#be123c]"><Sparkles className="h-4 w-4" />Buka VST Studio</Link><Link to="/vst-maker/partner" className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-5 py-3 text-xs font-black text-white hover:bg-white/[.05]"><UsersRound className="h-4 w-4" />Konsultasi Partner</Link></div>
          </div>
        </section>
      </div>
    </div>
  );
}
